import { useCallback, useMemo } from 'react'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { getCompanyId } from '@modules/search/components/ResultsSection/util/ResultProfile.util'
import { AnyProfile, AnyResultProfile, Company, CompanyAsset, isCompany, isDelegate } from '@modules/search/types'
import shortlistApi from '@modules/shortlist/api'
import {
  ShortlistExtendedProfile,
  ShortlistFilterField,
  ShortlistPageDataProps,
  ShortlistProfileData,
  ShortlistTableDataProps,
} from '@modules/shortlist/types'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { DEFAULT_SHORTLIST_PROFILES_PER_PAGE, SHORTLIST_STATUS_MAP } from '../constants'
import { ShortlistToProfileMap, shortlistProfileMap } from '../utils/createShortlistProfileMap'
import { DEFAULT_SHORTLIST_API_SORT, MAP_FIELD_NAME_TO_API_NAME } from './useSortProfileValuesMap'

const getProfileTitle = (profile: AnyResultProfile) => {
  if (isCompany(profile)) {
    return profile.latest.name
  }
  if (isDelegate(profile)) {
    return `${profile.latest.title} ${profile.latest.firstName} ${profile.latest.lastName}`
  }
  return profile.latest.title
}

const getUpdatedAt = (dateString?: string) => {
  return dateString ? new Date(dateString).getTime() : 0
}

const getAlertProfileUpdatedAt = (profile: ShortlistProfileData) => {
  const newAssetsAddedAt = (profile.profileMetadata as unknown as Company).assetLatestCreatedAt
  const fieldsUpdatedAt = profile.profileMetadata.latest.followedFieldsUpdatedAt

  return Math.max(getUpdatedAt(newAssetsAddedAt), getUpdatedAt(fieldsUpdatedAt))
}

export const useShortlists = () => {
  const { user } = useAuthContext()

  const {
    data = [],
    isLoading,
    isFetching,
    isInitialLoading,
  } = useQuery({
    queryKey: [shortlistApi.getShortlists.key, user?.id],
    queryFn: shortlistApi.getShortlists.fn,
    enabled: !!user?.id,
    placeholderData: keepPreviousData,
  })

  const rawShortlists = data.filter(i => i.type !== 'alert')

  const alertsShortlist = useMemo(() => data.find(i => i.type === 'alert'), [data])

  const shortlists = useMemo(() => {
    const list = rawShortlists.filter(i => i.status === SHORTLIST_STATUS_MAP.ENABLED && i.type !== 'alert')
    /**
     * we don't need this logic on FE, BE handles that, but decided to leave it for now to not break something
     * */
    if (typeof user?.p1PlusAccessConfig?.shortlistLimit === 'number') {
      return list.slice(0, user?.p1PlusAccessConfig?.shortlistLimit)
    }

    return list
  }, [rawShortlists, user])

  const isUniqueName = useCallback(
    (rawName: string) => {
      const name = rawName?.trim()

      if (!name) {
        return false
      }

      const isUnique = !rawShortlists.some(sh => sh.name?.toLowerCase() === name.toLowerCase())

      if (!isUnique) {
        return false
      }

      return true
    },
    [rawShortlists],
  )
  return {
    alertsShortlist,
    shortlists,
    rawShortlists,
    isUniqueName,
    isLoading,
    isFetching,
    isInitialLoading,
    canAddShortlists:
      typeof user?.p1PlusAccessConfig?.shortlistLimit !== 'number' ||
      rawShortlists.length < user?.p1PlusAccessConfig?.shortlistLimit,
  }
}

export const useShortlistPageData = ({
  shortlistId,
  isEmptyShortlist,
  size = DEFAULT_SHORTLIST_PROFILES_PER_PAGE,
  from = 0,
  sort,
  searchQuery = '',
  filters,
}: ShortlistPageDataProps): ShortlistTableDataProps => {
  const mappedSortField = (sort?.customFieldId || MAP_FIELD_NAME_TO_API_NAME[sort?.fieldName ?? '']) ?? sort?.fieldName

  const apiSort = {
    field: String(mappedSortField || DEFAULT_SHORTLIST_API_SORT.field),
    order: sort?.sortOrder?.toLowerCase() || DEFAULT_SHORTLIST_API_SORT.order,
  }

  const apiFilters = (filters ?? []).map(filter => ({
    value: filter.value,
    combinator: filter.combinator || undefined,
    operator: filter.operator,
    nestedAttribute: filter.nestedAttribute,
    attribute: filter.type === ShortlistFilterField.customFieldValues ? ShortlistFilterField.customFieldValues : filter.attribute,
  }))

  const {
    data: { total: totalProfilesAmount, result: profilesList = [] } = {},
    isLoading,
    isInitialLoading,
    isRefetching,
  } = useQuery({
    queryKey: [
      shortlistApi.getShortlistPageData.key,
      shortlistId,
      size,
      from,
      JSON.stringify(apiSort),
      searchQuery,
      sort?.customFieldId,
      JSON.stringify(apiFilters),
    ],
    queryFn: () =>
      shortlistApi.getShortlistPageData.fn({
        id: shortlistId,
        size,
        from,
        sort: apiSort,
        searchQuery,
        customFieldSortId: sort?.customFieldId,
        filters: apiFilters,
      }),
    placeholderData: keepPreviousData,
    enabled: typeof shortlistId === 'number' && !isEmptyShortlist,
  })

  /**
   * @TODO: refactor, this map creates some overhead for maintaining the structure
   * which is not necessary at all, profiles should be always profiles with the same schema
   * */
  const profilesPageData = useMemo(
    () =>
      profilesList
        .map<ShortlistExtendedProfile>(profileItem => {
          return {
            profileId: profileItem.id,
            globalId: profileItem.profileMetadata.id,
            profileType: profileItem.profileType,
            profileStatus: profileItem.status,
            profileImgData: profileItem.profileMetadata.latest,
            profileAlertUpdatedAt: getAlertProfileUpdatedAt(profileItem),
            profileHasPublicEvent: !!profileItem.profileMetadata.hasPublicProfile,
            profileHasSharedInfo: profileItem.hasSharedInfo,
            profileTitle: getProfileTitle(profileItem.profileMetadata),
            profileEventName: profileItem.profileMetadata.latest?.eventData?.name,
            profileEventStartDate: profileItem.profileMetadata.latest?.eventData?.startDate,
            profileEventEndDate: profileItem.profileMetadata.latest?.eventData?.endDate,
            profileEvents: profileItem.profileMetadata.events,
            profileUpdatedDate: profileItem.profileMetadata.latest?.updatedAt,
            alertUpdatedAt: profileItem.alertUpdatedAt,
            profileCreatedDate: profileItem.createdAt,
            profileLatestCompany: profileItem.profileMetadata.latestCompany,
            hasContacted: profileItem.hasContacted ?? false,
            isContactable: profileItem.profileMetadata.isContactable ?? true,
            companyId: profileItem.companyId || getCompanyId(profileItem.profileMetadata),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore:next-line
            companyName: profileItem.profileMetadata.latest.companyName,
            nda: profileItem?.nda ?? null,
            note: profileItem?.note ?? null,
            priority: profileItem?.priority,
            profileDevPhase: (profileItem.profileMetadata.latest as CompanyAsset)?.devPhase,
            tagId: profileItem.tagId || undefined,
            customFieldValues: profileItem.customFieldValues,
            createdAt: profileItem.createdAt,
          }
        })
        .filter(Boolean),
    [profilesList],
  )

  return {
    profilesPageData,
    isPageDataLoading: isInitialLoading || (isLoading && !isRefetching),
    totalProfilesAmount,
  }
}

export const useShortlistMap = () => {
  const { shortlists, isUniqueName, canAddShortlists } = useShortlists()

  const getKey = useCallback(
    (profile: { '@type': AnyProfile['@type']; id: AnyProfile['id'] | string }) => `${profile['@type']}_${profile.id}`,
    [],
  )

  const map = useMemo<ShortlistToProfileMap>(() => {
    if (!shortlists?.length) {
      return new Map() as ShortlistToProfileMap
    }

    return shortlistProfileMap(shortlists)
  }, [shortlists])

  return { map, shortlists, canAddShortlists, isUniqueName, getKey }
}
